import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";

const API = process.env.REACT_APP_PRODUCT_SERVICE_URL;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get("q") || "";
  const condition = searchParams.get("condition") || "";
  const minPrice = searchParams.get("minPrice") || "";
  const maxPrice = searchParams.get("maxPrice") || "";
  const pageParam = parseInt(searchParams.get("page") || "0", 20);

  const [products, setProducts] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalPages: 0,
    totalElements: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ---------- FETCH PRODUCTS ---------- */
  const fetchProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        q: query,
        page: pageParam,
        size: 12,
      });

      if (condition) params.append("condition", condition);
      if (minPrice) params.append("minPrice", minPrice);
      if (maxPrice) params.append("maxPrice", maxPrice);

      const res = await fetch(`${API}/products/public/search?${params}`);

      if (!res.ok) throw new Error("Failed to fetch products");

      const data = await res.json();

      // Compatible with Spring VIA_DTO
      const content = data?.content || [];
      const page = data?.page || {};

      // hide out-of-stock products on frontend
      const filtered = content.filter(
        (p) => Number(p.availableQuantity || 0) > 0,
      );

      setProducts(filtered);

      setPageInfo({
        totalPages: page.totalPages || 0,
        totalElements: page.totalElements || 0,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (query) fetchProducts();
  }, [query, condition, minPrice, maxPrice, pageParam]);

  /* ---------- UPDATE SEARCH PARAMS ---------- */
  const updateParams = (updates) => {
    const newParams = {
      q: query,
      condition,
      minPrice,
      maxPrice,
      page: 0,
      ...updates,
    };

    setSearchParams(newParams);
  };

  return (
    <div className="container py-4">
      {/* ---------- HEADER ---------- */}
      <div className="mb-4">
        <h4>
          Search Results for "<strong>{query}</strong>"
        </h4>
        <small className="text-muted">
          {pageInfo.totalElements} products found
        </small>
      </div>

      {/* ---------- FILTERS ---------- */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <select
            className="form-select"
            value={condition}
            onChange={(e) => updateParams({ condition: e.target.value })}
          >
            <option value="">All Conditions</option>
            <option value="NEW">New</option>
            <option value="USED_LIKE_NEW">Used - Like New</option>
            <option value="USED_GOOD">Used - Good</option>
            <option value="USED_FAIR">Used - Fair</option>
            <option value="REFURBISHED">Refurbished</option>
          </select>
        </div>

        <div className="col-md-3">
          <input
            type="number"
            placeholder="Minimum Price"
            className="form-control"
            value={minPrice}
            onChange={(e) => updateParams({ minPrice: e.target.value })}
          />
        </div>
        <div className="col-md-3">
          <input
            type="number"
            placeholder="Maximum Price"
            className="form-control"
            value={maxPrice}
            onChange={(e) => updateParams({ maxPrice: e.target.value })}
          />
        </div>
      </div>

      {/* ---------- LOADING ---------- */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      )}

      {/* ---------- ERROR ---------- */}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* ---------- EMPTY STATE ---------- */}
      {!loading && products.length === 0 && (
        <div className="text-center py-5 text-muted">No products found.</div>
      )}

      {/* ---------- PRODUCT GRID ---------- */}
      <div className="row g-4">
        {products.map((product) => (
          <div key={product.id} className="col-6 col-md-4 col-lg-3">
            <ProductCard product={product} />
          </div>
        ))}
      </div>

      {/* ---------- PAGINATION ---------- */}
      {pageInfo.totalPages > 1 && (
        <div className="d-flex justify-content-center mt-5">
          {Array.from({ length: pageInfo.totalPages }).map((_, i) => (
            <button
              key={i}
              className={`btn btn-sm mx-1 ${
                i === pageParam ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => updateParams({ page: i })}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
